import { useState } from 'react';
import { HelpCircle, Globe, Phone, Menu, X } from 'lucide-react';
import { PORTAL } from '@/constants/testIds';
import { PHONE_NUMBER, PHONE_TEL } from '@/i18n/content';

const ZMEX_LOGO =
  'https://customer-assets.emergentagent.com/job_zmex-pay/artifacts/8o0hfnc6_ZMEXLOGO.png';

/**
 * Dark-navy portal header used on landing, verify, and dashboard pages.
 * Markup is identical to the approved landing-page header — do not
 * redesign.
 */
export function PortalHeader({ t, lang, onToggleLang }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="w-full bg-[#0B1930] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <div
            className="flex items-center gap-3 sm:gap-4"
            data-testid={PORTAL.headerLogo}
          >
            <img
              src={ZMEX_LOGO}
              alt="ZMEX Disposal"
              className="h-14 sm:h-16 lg:h-20 w-auto select-none"
              draggable="false"
            />
            <div className="hidden sm:flex flex-col leading-tight border-l border-white/15 pl-3 sm:pl-4">
              <span className="font-heading text-[10px] sm:text-[11px] tracking-[0.22em] text-white/60 uppercase">
                {t.header.portalName}
              </span>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-2 xl:gap-3">
            <button
              type="button"
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white/85 hover:text-white hover:bg-white/5 transition-colors"
              data-testid={PORTAL.headerHelp}
            >
              <HelpCircle className="h-4 w-4" />
              {t.header.help}
            </button>
            <span className="h-6 w-px bg-white/15" aria-hidden="true" />
            <button
              type="button"
              onClick={onToggleLang}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white/85 hover:text-white hover:bg-white/5 transition-colors"
              data-testid={PORTAL.headerLangToggle}
              aria-label={
                lang === 'en' ? 'Switch to Spanish' : 'Switch to English'
              }
            >
              <Globe className="h-4 w-4" />
              {t.langToggleLabel}
            </button>
            <span className="h-6 w-px bg-white/15" aria-hidden="true" />
            <a
              href={`tel:${PHONE_TEL}`}
              className="group flex items-center gap-2.5 pl-4 pr-1 py-1.5 text-sm text-white/85 hover:text-white transition-colors"
              data-testid={PORTAL.headerPhone}
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/8 group-hover:bg-[#C8A46B]/20 transition-colors">
                <Phone className="h-4 w-4 text-[#C8A46B]" />
              </span>
              <span className="whitespace-nowrap">
                {t.header.needHelpPrefix}{' '}
                <span className="font-semibold text-[#C8A46B]">
                  {PHONE_NUMBER}
                </span>
              </span>
            </a>
          </nav>

          <div className="flex lg:hidden items-center gap-2">
            <a
              href={`tel:${PHONE_TEL}`}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-[#C8A46B]/20 transition-colors"
              aria-label={`Call ${PHONE_NUMBER}`}
              data-testid={`${PORTAL.headerPhone}-mobile`}
            >
              <Phone className="h-4 w-4 text-[#C8A46B]" />
            </a>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/15 transition-colors"
              aria-label="Menu"
              data-testid={PORTAL.headerMobileMenuBtn}
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden pb-5 pt-1 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm font-medium text-white/90 hover:bg-white/5 transition-colors"
              data-testid={`${PORTAL.headerHelp}-mobile`}
            >
              <HelpCircle className="h-4 w-4" />
              {t.header.help}
            </button>
            <button
              type="button"
              onClick={() => {
                onToggleLang();
                setMobileOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm font-medium text-white/90 hover:bg-white/5 transition-colors"
              data-testid={`${PORTAL.headerLangToggle}-mobile`}
            >
              <Globe className="h-4 w-4" />
              {t.langToggleLabel}
            </button>
            <a
              href={`tel:${PHONE_TEL}`}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-white/90 hover:bg-white/5 transition-colors"
            >
              <Phone className="h-4 w-4 text-[#C8A46B]" />
              <span>
                {t.header.needHelpPrefix}{' '}
                <span className="font-semibold text-[#C8A46B]">
                  {PHONE_NUMBER}
                </span>
              </span>
            </a>
          </div>
        )}
      </div>
    </header>
  );
}

/** Simple footer shared across portal pages. */
export function PortalFooter({ t }) {
  return (
    <footer className="relative z-10 border-t border-[#EDE5D4] bg-[#F5F1EA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-[#4B5563]">
          © {new Date().getFullYear()} {t.footer.rights}
        </p>
        <a
          href={`tel:${PHONE_TEL}`}
          className="text-xs font-medium text-[#4B5563] hover:text-[#0B1930] transition-colors"
        >
          {t.header.needHelpPrefix}{' '}
          <span className="text-[#B8925A] font-semibold">{PHONE_NUMBER}</span>
        </a>
      </div>
    </footer>
  );
}
